const generation = {
    system: 'OpenAI Codex AI assistant',
    date: '18 August 2026',
    status: 'Human review pending',
    note: 'The AI synthesized the content from the listed biblical, scholarly, and ecclesial sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
};

const source = (id, author, title, detail, href) => ({ id, author, title, detail, href });

export const holinessPerspective = {
    slug: 'what-is-holiness',
    category: 'Christian Formation',
    question: 'What is holiness?',
    shortTitle: 'What holiness means in the Bible',
    summary: 'Holiness first describes God, yet Scripture also calls people, places, times, practices, and communities holy because they belong to God. Christians agree that human holiness is received by grace and expressed in a changed life, but differ over whether its centre is God’s unique character, consecrated belonging, Christlikeness, participation in divine life, or perfect love.',
    introduction: 'This question is broader than “What is sanctification?”, which asks how people are made holy, and “How should holiness be recognised?”, which asks what evidence holiness produces. Here the focus is the meaning of holiness itself. The readings overlap: separation from evil is not isolation from neighbour, belonging to God creates a vocation, and moral transformation depends on God’s prior gift and presence.',
    seoDescription: 'Explore what holiness means in the Bible through God’s character, consecrated belonging, Christlikeness, participation in God, and perfect love.',
    lastModified: '2026-08-18',
    keyTexts: [
        { label: 'Exodus 19:3–6', href: '/read/Exod.19/#Exod.19.3-Exod.19.6' },
        { label: 'Leviticus 19:1–18', href: '/read/Lev.19/#Lev.19.1-Lev.19.18' },
        { label: 'Isaiah 6:1–8', href: '/read/Isa.6/#Isa.6.1-Isa.6.8' },
        { label: '1 Peter 1:13–25', href: '/read/1Pet.1/#1Pet.1.13-1Pet.1.25' },
        { label: 'Hebrews 12:14–17', href: '/read/Heb.12/#Heb.12.14-Heb.12.17' },
    ],
    readings: [
        {
            id: 'gods-holy-identity',
            title: 'God’s holiness comes first',
            thesis: 'Holiness first names the unique, free, and morally perfect life of the triune God, who graciously communicates a creaturely echo of that holiness to the church and its people.',
            accent: 'navy',
            badges: ['Reformed', 'Classical Theology', 'Ecumenical'],
            summary: 'This reading resists beginning with a human programme of purity. God is holy in himself: incomparable to creation, entirely faithful and good, opposed to evil, and free to give himself in grace. People do not possess holiness independently; they become holy as God elects, reconciles, and orders them toward worship, witness, and faithful discipleship.',
            contribution: 'It keeps holiness centred on God rather than spiritual experience, moral achievement, or social respectability. Human holiness becomes a response to God’s saving action and a reflection of his character rather than a self-made status.',
            caution: 'Language about divine transcendence can become abstract if it is detached from the biblical story of God drawing near in covenant, Christ, and the Spirit. The view must show how God’s holiness creates concrete forms of love, justice, worship, and resistance to evil.',
            supporters: [
                { name: 'John Webster', sourceId: 'webster-holiness' },
            ],
            predicates: [
                {
                    label: 'Sanctification is God’s gracious work before it is human achievement',
                    topicSlug: 'what-is-sanctification',
                    readingId: 'progressive-holiness',
                    linked: true,
                },
                {
                    label: 'Holiness becomes visible through durable fruit and obedient faith',
                    topicSlug: 'how-should-holiness-be-recognised',
                    readingId: 'fruit-and-obedience',
                    linked: true,
                },
            ],
            relations: [
                { type: 'grounds', target: 'consecrated-belonging', linked: true },
                { type: 'grounds', target: 'christlike-spirit-formed-life', linked: true },
                { type: 'overlaps with', target: 'participation-in-divine-life', linked: true },
            ],
        },
        {
            id: 'consecrated-belonging',
            title: 'Holiness means belonging to God',
            thesis: 'To be holy is to belong to God as set apart for his presence and purposes, a gifted identity expressed through worship, justice, and covenant faithfulness.',
            accent: 'amber',
            badges: ['Biblical Theology', 'Jewish Context', 'Moderate'],
            summary: 'In the Hebrew Bible, holiness is associated with God and extends to what is brought into a distinctive relationship with him. Israel is called a holy people because it belongs to God and is commissioned to embody that relationship. Leviticus joins worship and ritual distinction to economic honesty, sexual integrity, care for vulnerable people, truthful judgement, and love of neighbour.',
            contribution: 'It prevents “set apart” from meaning merely private moral separation. Consecration establishes both identity and responsibility: holy people, places, and practices belong to God and must serve the purposes for which they are given.',
            caution: 'The language of separation has sometimes been used to justify superiority, exclusion, or withdrawal. This reading must preserve the biblical movement from divine election to service, ethical responsibility, and blessing rather than treating holiness as privileged distance from others.',
            supporters: [
                { name: 'Israel Knohl', sourceId: 'knohl-kedusha' },
            ],
            predicates: [
                {
                    label: 'God’s instruction continues to describe faithful covenant life',
                    topicSlug: 'what-can-the-law-do-if-it-cannot-justify',
                    readingId: 'guides-covenant-life',
                    linked: true,
                },
                {
                    label: 'Christ fulfils and transforms rather than simply discards Israel’s covenant vocation',
                    topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                    readingId: 'fulfilled-and-transformed',
                    linked: true,
                },
            ],
            relations: [
                { type: 'depends on', target: 'gods-holy-identity', linked: true },
                { type: 'is embodied by', target: 'christlike-spirit-formed-life', linked: true },
                { type: 'extends into', target: 'perfect-love-personal-social', linked: true },
            ],
        },
        {
            id: 'christlike-spirit-formed-life',
            title: 'Holiness looks like Christ',
            thesis: 'Human holiness is the character of Christ taking shape through Spirit-enabled purity, covenant love, obedience, worship, and mission.',
            accent: 'red',
            badges: ['Pentecostal', 'Evangelical', 'Charismatic'],
            summary: 'This reading brings holiness into direct relation with Jesus. What is holy looks, sounds, and acts like Christ: not only avoiding what corrupts, but positively displaying righteousness, love, humility, courage, truth, and service. The Spirit sets believers apart in Christ and continues to form their conduct so that received identity becomes a recognisable way of life.',
            contribution: 'It makes holiness concrete and personal without reducing it to unaided moral effort. Spiritual experience, ethical change, worship, and public witness belong together because the same Spirit conforms the whole person and community to Christ.',
            caution: 'Communities may confuse Christlikeness with their own cultural codes, personalities, or preferred spiritual experiences. Claims of holiness must therefore be tested by Scripture, mature character, accountable community, and the treatment of vulnerable people.',
            supporters: [
                { name: 'Assemblies of God educators', sourceId: 'ag-holiness' },
            ],
            predicates: [
                {
                    label: 'Believers are decisively set apart and continue becoming holy',
                    topicSlug: 'what-is-sanctification',
                    readingId: 'definitive-and-progressive',
                    linked: true,
                },
                {
                    label: 'The Spirit’s presence is tested through a whole pattern of life and witness',
                    topicSlug: 'how-should-holiness-be-recognised',
                    readingId: 'spirit-empowered-life',
                    linked: true,
                },
            ],
            relations: [
                { type: 'expresses', target: 'gods-holy-identity', linked: true },
                { type: 'embodies', target: 'consecrated-belonging', linked: true },
                { type: 'overlaps with', target: 'perfect-love-personal-social', linked: true },
            ],
        },
        {
            id: 'participation-in-divine-life',
            title: 'Holiness means sharing in God’s life',
            thesis: 'Holiness is grace-enabled participation in God’s life, healing people into communion with the Father through the Son in the Holy Spirit.',
            accent: 'teal',
            badges: ['Orthodox', 'Catholic', 'Sacramental', 'Ecumenical'],
            summary: 'This reading describes holiness as more than resemblance observed from a distance. Through Christ and the Spirit, people share in divine life without becoming God by nature. Prayer, sacrament, repentance, ascetic practice, mercy, and the church’s common life are means through which grace heals disordered desire and deepens communion with God and neighbour.',
            contribution: 'It presents holiness as relational, embodied, and communal. The goal is not merely rule-keeping or a private spiritual state, but restored communion and transformation into the likeness of God that reaches its fulfilment in glory.',
            caution: 'Participation language can be misunderstood as erasing the distinction between Creator and creature or as making ecclesial practices automatically effective. The view must keep participation dependent on grace and joined to repentance, freedom, and visible love.',
            supporters: [
                { name: 'Orthodox Church in America', sourceId: 'oca-trinitarian-life' },
                { name: 'Second Vatican Council', sourceId: 'lumen-gentium-holiness' },
            ],
            predicates: [
                {
                    label: 'Sanctification is participation in divine life through Christ and the Spirit',
                    topicSlug: 'what-is-sanctification',
                    readingId: 'participation-in-divine-life',
                    linked: true,
                },
                {
                    label: 'Holiness is recognised through growing communion, repentance, and mercy',
                    topicSlug: 'how-should-holiness-be-recognised',
                    readingId: 'participation-and-communion',
                    linked: true,
                },
            ],
            relations: [
                { type: 'deepens', target: 'gods-holy-identity', linked: true },
                { type: 'integrates', target: 'consecrated-belonging', linked: true },
                { type: 'overlaps with', target: 'perfect-love-personal-social', linked: true },
            ],
        },
        {
            id: 'perfect-love-personal-social',
            title: 'Holiness is shown in perfect love',
            thesis: 'Holiness is love of God and neighbour filling the heart and governing personal conduct, accountable community, mercy, justice, worship, and service.',
            accent: 'amber',
            badges: ['Wesleyan', 'Methodist', 'Holiness', 'Progressive'],
            summary: 'Wesleyan teaching locates holiness in a heart and life ordered by grace toward love. Personal holiness includes prayer, worship, integrity, self-control, and compassion; social holiness insists that love is practised with others and addresses harm, poverty, exclusion, and injustice. “Perfection” means maturity or fullness of love, not freedom from ignorance, weakness, or further growth.',
            contribution: 'It gives holiness a clear governing purpose and refuses to separate inward devotion from neighbour-love and public responsibility. Practices matter because grace uses them to form people and communities whose love becomes active and accountable.',
            caution: 'A community can identify love too closely with its own reform programme or imply that visible activism proves inward holiness. The account must keep love rooted in God’s grace, truth, repentance, and patient formation rather than sentiment or achievement.',
            supporters: [
                { name: 'The United Methodist Church', sourceId: 'umc-wesleyan-holiness' },
            ],
            predicates: [
                {
                    label: 'Entire sanctification is ordered toward perfect love of God and neighbour',
                    topicSlug: 'what-is-sanctification',
                    readingId: 'perfect-love',
                    linked: true,
                },
                {
                    label: 'Personal devotion and social responsibility are inseparable signs of holiness',
                    topicSlug: 'how-should-holiness-be-recognised',
                    readingId: 'personal-and-social-love',
                    linked: true,
                },
            ],
            relations: [
                { type: 'extends', target: 'christlike-spirit-formed-life', linked: true },
                { type: 'expresses', target: 'consecrated-belonging', linked: true },
                { type: 'overlaps with', target: 'participation-in-divine-life', linked: true },
            ],
        },
    ],
    sources: [
        source(
            'webster-holiness',
            'John Webster',
            'Holiness',
            'A trinitarian theological account grounding holiness in God’s identity, grace, and election and tracing its echo in church, worship, witness, and discipleship.',
            'https://www.eerdmans.com/9780802822154/holiness/',
        ),
        source(
            'knohl-kedusha',
            'Israel Knohl',
            'The Concept of Kedusha (Sanctity)',
            'A Hebrew Bible study of holiness as derived from God and expressed through consecration, worship, ethics, justice, and Israel’s covenant vocation.',
            'https://www.thetorah.com/article/the-concept-of-kedusha-sanctity',
        ),
        source(
            'ag-holiness',
            'Assemblies of God educators',
            'Holiness: Living Like Christ',
            'A Pentecostal discussion defining human holiness through Christlikeness, moral purity, covenant love, consecration, and Spirit-formed conduct.',
            'https://enrichmentjournal.ag.org/Issues/2011/Fall-2011/Holiness',
        ),
        source(
            'oca-trinitarian-life',
            'Orthodox Church in America',
            'The Holy Trinity in Christian Life',
            'An Orthodox account of holiness as Spirit-enabled participation in the life, love, goodness, and holiness of the triune God.',
            'https://www.oca.org/orthodoxy/the-orthodox-faith/doctrine-scripture/the-holy-trinity/the-holy-trinity-in-christian-life',
        ),
        source(
            'lumen-gentium-holiness',
            'Second Vatican Council',
            'Dogmatic Constitution on the Church: Lumen Gentium, Chapter V',
            'An official Catholic account of the universal call to holiness, participation in Christ, the Spirit’s work, and the perfection of charity.',
            'https://www.vatican.va/archive/hist_councils/ii_vatican_council/documents/vat-ii_const_19641121_lumen-gentium_en.html',
        ),
        source(
            'umc-wesleyan-holiness',
            'The United Methodist Church',
            'Our Wesleyan Heritage',
            'A Methodist account of holiness of heart and life, sanctifying grace, perfect love, accountable fellowship, worship, mercy, and justice.',
            'https://www.umc.org/en/content/our-wesleyan-heritage',
        ),
    ],
    generation,
};
